import _ from "lodash";
import { showAlert } from "helper";
import { v4 as uuidv4 } from 'uuid';
import { Box } from "@mui/material";
import styled from 'styled-components';
import FormField from "components/FormField";
import TextButton from "components/TextButton";
import React, { useState, useEffect } from "react";
import SidebarHeader from "../layout/SidebarHeader";
import { DeleteOutline } from "@mui/icons-material";
import useProjectStore from '../../../store-zustand/project';
import TooltipIconButton from "components/TooltipIconButton";

const Header = styled.div`
  margin: 0 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  h2 {
    font-size: 18px;
    line-height: 22px;
    color: #E5E5E5;
    font-weight: 400;
  }
`;

const Description = styled.div`
  p {
    color: #C4C4C4;

    &:not(.contact) {
      margin-bottom: 15px;
    }

    a {
      text-decoration: underline;
    }
  }
`;

const EndpointList = styled.div`
  margin-top: 27px;

  h2 {
    font-size: 18px;
    line-height: 22px;
    color: #E5E5E5;
    font-weight: 400;
    margin-bottom: 10px;
  }

  .header-box {
    border-top: 1px solid #A0A0A0;
    border-bottom: 1px solid #A0A0A0;
    font-weight: 400;
    font-size: 9px;
    line-height: 11px;
    color: white;

    .endpoint-delete {
      width: 10%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .endpoint-type {
      width: 10%;
    }

    .endpoint-name {
      width: 50%;
    }

    .endpoint-project {
      width: 30%;
      text-align: left;
    }  
  }

  .endpoint-item {
    border-bottom: 1px solid #A0A0A0;
    color: #C4C4C4;
    line-height: 11px;
    font-size: 9px;
    font-weight: 400;

    .endpoint-delete {
      width: 10%;
      display: flex;
      align-items: center;
      justify-content: center;

      svg {
        font-size: 1.4rem !important;
        cursor: pointer;
      }
    }

    .endpoint-type {
      width: 10%;
    }

    .endpoint-name {
      width: 50%;
    }

    .endpoint-project {
      width: 30%;
      text-align: left;
    }  
  }
`;

const types = [
  { id: 1, label: 'WMS (Web Map Service)', value: 'WMS', active: true },
  { id: 2, label: 'WMTS (Web Map Tiled Service)', value: 'WMTS', active: true },
  { id: 3, label: 'TMS (Tile Map Service)', value: 'TMS', active: true },
  { id: 4, label: '3D Tiles', value: '3DTile', active: false }
];

const WebMapService = ({ goToProfilePage }) => {
  const [projects, setProjects] = useState([]);
  const [endpointList, setEndpointList] = useState([]);
  const [loadingTypes, setloadingTypes] = useState(undefined);
  const [newService, setNewService] = useState({ type: '', name: '', endpoint: '', project: '', api_key: null, description: null });
  const { getProjectAssets, deleteProjectAsset, GetProjectTypeList, GetProjectListByType, addAssetToProject } = useProjectStore();
  
  const handleChangeService = (e, name) => {
    setNewService({ ...newService, [name]: e.target.value });
  };

  const isValidUrl = (url) => {
    const regex = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;
    return !!regex.test(url)
  };

  const connectService = () => {
    const endpoint = { ...newService, id: uuidv4() };
    
    endpoint.project  = endpoint.project ? endpoint.project : projects[0].id;
    const asset_type = types.find(item => item.id.toString() === endpoint.type);
    endpoint.type = asset_type ? asset_type.value : types[0].value;
    if (endpoint.name === '') {
      showAlert('error', 'Please enter a name for this service.');
      return;
    }

    if (endpoint.endpoint === '') {
      showAlert('error', 'Please enter a url for this service.');
      return;
    }

    if (!isValidUrl(endpoint.endpoint)) {
      showAlert('error', 'Please enter a valid url for this service.');
      return;
    }

    addAssetToProject(null, endpoint.type, endpoint.api_key, endpoint.project, endpoint.name, endpoint.endpoint, endpoint.description).then((response) => {
      if (response && response !== 500 && response !== 404) {
        showAlert('success', 'Web Map Service has been successfully added to the project!');
        getPorjectsAssets();
      } else {
        showAlert('error', 'Failed to save asset to the project.');
      }
    })
  };

  const deleteAsset = (endpoint) => {
    deleteProjectAsset(endpoint.id).then(() => {
      showAlert('success', 'Item has been successfully removed.');
      const asset = window.assets[endpoint.id];
      if (asset !== undefined) window.MAIN_CESIUM_VIEWER.imageryLayers.remove(asset);
      getPorjectsAssets();
    }).catch((e) => showAlert('error', `Failed to delete asset from the project. ${e}`))
  }

  const getPorjectsAssets = async () => {
    setEndpointList([]);
    try {
      const assetPromises = projects.map(project => 
        getProjectAssets(project.id).then((response) => {
          if (response.assets) {
            return response.assets.filter(asset => types.some(type => type.value === asset.type))
              .map(asset => ({
                id: asset.ID,
                type: asset.type,
                name: asset.name,
                endpoint: asset.url,
                project: project.id,
                api_key: asset.tokenKey,
                description: asset.description
              }));
          }
          return [];
        })
      );
      const allAssets = await Promise.all(assetPromises);
      const existing_assets = allAssets.flat();
      setEndpointList(prev => [...prev, ...existing_assets]);
    } catch {
      showAlert('error', 'Error fetching project assets.');
    }
  }

  useEffect(() => { 
    if (!projects || projects.length === 0) return;

    getPorjectsAssets();
  }, [projects])

  useEffect(() => {
    async function fetchData() {
      if (loadingTypes !== undefined) return;
      const PROJECTSTypes = await GetProjectTypeList();
      if (PROJECTSTypes !== 'Error') {
        setloadingTypes(PROJECTSTypes);
        let response = await Promise.all(PROJECTSTypes.map(async ({ pk, fields }, index) => {
          const type = {
            IDTYPE: pk
          }
          const projects_list = await GetProjectListByType(type);
          return projects_list;
        }));
        setProjects(_.flatten(response).map(item => {
          return { id: item.pk, label: (item.fields.NAME === null || item.fields.NAME === 'null') ? 'Untitled' : item.fields.NAME }
        }));
      } else {
        showAlert('error', 'Failed to get list of Web Map Service Types.');
      }
    }
    fetchData();
  }, [loadingTypes]);

  return (
    <>
      <SidebarHeader onClose={goToProfilePage} />
      <>
        <Header>
          <h2>Web Map Service</h2>
          <TextButton text="CONNECT" onClick={connectService}/>
        </Header>
        <FormField label="Type" id="asset_type">
          <select id="asset_type" onChange={(e) => handleChangeService(e, 'type')}>
            {types.map((type, index) => (
              <option value={type.id} key={index} disabled={!type.active}>{type.label}</option>
            ))}        
          </select>
        </FormField>
        <FormField label="Name">
          <input type="text" placeholder="Enter Service Name" value={newService.name} onChange={(e) => handleChangeService(e, 'name')} />
        </FormField>
        <FormField label="URL">
          <input type="text" placeholder="Enter Service URL" value={newService.endpoint} onChange={(e) => handleChangeService(e, 'endpoint')} />
        </FormField>
        <FormField label="API Key">
          <input type="text" placeholder="Enter Service API Key (Optional)" value={newService.api_key} onChange={(e) => handleChangeService(e, 'api_key')} />
        </FormField>
        <FormField label="Description">
          <input type="text" placeholder="Enter Service Description (Optional)" value={newService.description} onChange={(e) => handleChangeService(e, 'description')} />
        </FormField>
        <FormField label="Project">
          <select id="project" onChange={(e) => handleChangeService(e, 'project')}>
            {projects.map((project, index) => (
              <option value={project.id} key={index}>{project.label}</option>
            ))}        
          </select>
        </FormField>
        <Description>
          <p>Copy and paste the endpoint to connect web map service. WMTS, TMS and WMS are supported.</p>
          <p className="contact">Looking for a web map Service Provider?  <a href="mailto:sales@fastmap.ca">Contact Sales</a></p>
        </Description>
        <EndpointList>
          <h2>Existing Services</h2>
          <Box display="flex" alignItems="center" justifyContent="space-between" className="header-box" padding="7px 7px 7px 0">
            <div className="endpoint-type">TYPE</div>
            <div className="endpoint-name">NAME</div>
            <div className="endpoint-project">PROJECT</div>
            <div className="endpoint-delete">DELETE</div>
          </Box>
          {endpointList.map((endpoint, index) => (
            <Box display="flex" alignItems="center" justifyContent="space-between" className="endpoint-item" padding="7px 7px 7px 0" key={index}>
              <div className="endpoint-type">{endpoint.type}</div>
              <div className="endpoint-name">{endpoint.name}</div>
              <div className="endpoint-project">{endpoint.project !== '' ? projects.find(project => project.id.toString() === endpoint?.project.toString())?.label : 'None'}</div>
              <div className="endpoint-delete">
                <TooltipIconButton title="Delete Service" ariaLabel="delete-service" placement="right" size={24} onClick={() => deleteAsset(endpoint)}>
                  <DeleteOutline fontSize="small"/>
                </TooltipIconButton> 
              </div>
            </Box>
          ))}
        </EndpointList>
      </> 
    </>
  );
};

export default WebMapService;